import { Head, Link } from "@inertiajs/react"

import { WorkflowStatusBadge, deriveWorkflowStatus } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Expense {
  id: number
  expense_date: string | null
  user_name: string | null
  expense_type: string | null
  other_type: string | null
  amount: number
  paid_by: string | null
  notes: string | null
  has_receipt: boolean
  receipt_url: string | null
  submitted: boolean
  approved: boolean
  paid: boolean
}

interface Props {
  customer: Customer
  expenses: Expense[]
}

export default function Index({ customer, expenses }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Expenses", href: `/customers/${customer.id}/expenses` },
  ]

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const statusBadge = (expense: Expense) => (
    <WorkflowStatusBadge status={deriveWorkflowStatus(expense)} />
  )

  const totalAmount = expenses.reduce((sum, e) => sum + e.amount, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Expenses - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Expenses for {customer.display_name}
          </h1>
          <Link
            href={`/customers/${customer.id}/expenses/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white"
          >
            Add Expense
          </Link>
        </div>

        {expenses.length > 0 && (
          <div className="mb-4 rounded-lg bg-white p-4 shadow">
            <span className="text-hpi-med-gray text-sm">Total: </span>
            <span className="text-lg font-bold">
              {formatCurrency(totalAmount)}
            </span>
            <span className="text-hpi-med-gray ml-4 text-sm">
              {expenses.length} {expenses.length === 1 ? "expense" : "expenses"}
            </span>
          </div>
        )}

        {expenses.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Employee
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Notes
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Amount
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Receipt
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/expenses/${expense.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {expense.expense_date}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/expenses/${expense.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {expense.expense_type}
                    </Link>
                    {expense.other_type && (
                      <div className="text-hpi-med-gray text-xs">
                        {expense.other_type}
                      </div>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {expense.user_name}
                  </td>
                  <td className="border-hpi-light-gray max-w-xs truncate border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/expenses/${expense.id}`}
                      className="hover:underline"
                    >
                      {expense.notes}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {formatCurrency(expense.amount)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {expense.receipt_url ? (
                      <a
                        href={expense.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:underline"
                      >
                        View
                      </a>
                    ) : expense.has_receipt ? (
                      <span className="text-hpi-dark-green">Yes</span>
                    ) : (
                      <span className="text-hpi-med-gray">-</span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {statusBadge(expense)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No expenses found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Customer
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
